/**
 * 接口 JSON 键名转换（与 iOS KPJSON 一致）：外层字段 snake_case ↔ camelCase，
 * 但"原样透传"的字段（payload、progress 等）的值保持不变，里面的键不转换。
 */

/** 这些字段的值是任意 JSON，内部键不做转换 */
export const OPAQUE_KEYS: ReadonlySet<string> = new Set(["payload", "progress", "result", "condition", "reward", "params", "state", "detail", "overrides", "params_schema"]);
/**
 * 编码（请求体）时 data 也透传（事件的 data）。解码时不透传：/v1/sync 响应的外层也叫 data。
 */
export const OPAQUE_ENCODE_KEYS: ReadonlySet<string> = new Set([...OPAQUE_KEYS, "data"]);

export function snakeToCamel(s: string): string {
  if (!s.includes("_")) return s;
  return s
    .split("_")
    .map((p, i) => (i === 0 ? p : p.charAt(0).toUpperCase() + p.slice(1)))
    .join("");
}

export function camelToSnake(s: string): string {
  return s.replace(/[A-Z]/g, (ch) => `_${ch.toLowerCase()}`);
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v) && Object.getPrototypeOf(v) === Object.prototype;
}

function convert(v: unknown, rename: (k: string) => string, opaque: ReadonlySet<string>): unknown {
  if (Array.isArray(v)) return v.map((x) => convert(x, rename, opaque));
  if (!isPlainObject(v)) return v;
  const out: Record<string, unknown> = {};
  for (const [k, val] of Object.entries(v)) {
    out[rename(k)] = opaque.has(k) ? val : convert(val, rename, opaque);
  }
  return out;
}

/** 接口响应 → 页面使用的对象（snake_case → camelCase） */
export function fromApi<T = unknown>(v: unknown): T {
  return convert(v, snakeToCamel, OPAQUE_KEYS) as T;
}

/** 页面对象 → 请求体（camelCase → snake_case）。undefined 字段会被 JSON.stringify 去掉；要清空字段请传 null */
export function toApi(v: unknown): unknown {
  return convert(v, camelToSnake, OPAQUE_ENCODE_KEYS);
}
